import {
  BulkResult,
  GroupMembershipResponse,
  partitionBulkResult,
} from '@aslanpay/portal-common-lib';

export interface FailedMember {
  id: string;
  name: string;
  reason: string;
}

const DEFAULT_REASON = 'This member could not be added. Please try again.';

/**
 * Collect the failed rows across one or more group-membership bulk results into a display list, resolving each
 * row's individualId to a name. Rows are keyed by individualId (see ASD-4265); a missing name falls back to the
 * id, and a missing reason to a generic client-safe message.
 */
export const collectFailedMembers = (
  results: (BulkResult<GroupMembershipResponse> | undefined)[],
  nameById: (id: string) => string | undefined
): FailedMember[] =>
  results
    .flatMap((result) => partitionBulkResult(result).failed)
    .map((row) => ({
      id: row.id,
      name: nameById(row.id) ?? row.id,
      reason: row.error?.message ?? DEFAULT_REASON,
    }));

/**
 * Total number of rows across the given bulk results that succeeded.
 */
export const countSucceeded = (
  results: (BulkResult<GroupMembershipResponse> | undefined)[]
): number => results.reduce((total, result) => total + partitionBulkResult(result).successCount, 0);

export interface MembershipCallOutcome {
  /** The BulkResult when the call returned 200 (with per-row outcomes). */
  result?: BulkResult<GroupMembershipResponse>;
  /** Individual ids of a call that failed wholesale (network / 5xx) — none of its rows applied. */
  rejectedIds?: string[];
}

export interface MembershipOutcome {
  failed: FailedMember[];
  succeededCount: number;
}

/**
 * Combine the settled outcomes of one or more membership calls (each either a 200 BulkResult, or a wholesale
 * rejection whose rows all failed) into a single per-member outcome: the failed members (with names/reasons)
 * and how many rows succeeded.
 */
export const resolveMembershipOutcomes = (
  outcomes: MembershipCallOutcome[],
  nameById: (id: string) => string | undefined
): MembershipOutcome => {
  const failedFromResults = collectFailedMembers(
    outcomes.map((outcome) => outcome.result),
    nameById
  );
  const failedFromRejections: FailedMember[] = outcomes
    .flatMap((outcome) => outcome.rejectedIds ?? [])
    .map((id) => ({ id, name: nameById(id) ?? id, reason: DEFAULT_REASON }));

  return {
    failed: [...failedFromResults, ...failedFromRejections],
    succeededCount: countSucceeded(outcomes.map((outcome) => outcome.result)),
  };
};
